import React, { useEffect, useState, useMemo } from 'react';
import { Navigate, useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Shield,
  LogOut,
  Users,
  FileText,
  Package,
  ShoppingCart,
  Key,
  Palette,
  Bell,
  Building2,
  CreditCard,
  Mail,
  Webhook,
  Loader2,
  Settings,
  UserCircle,
  ScrollText,
  LifeBuoy,
  FileCheck2,
  Activity,
  ChevronDown,
  ChevronRight,
  Plug,
  Megaphone,
  MonitorCheck,
  CheckCircle2,
  AlertTriangle,
  Rocket,
  BookOpen,
  Inbox,
  MessageSquare,
  Wallet,
  Search,
} from 'lucide-react';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { canAccessTab, ROLE_LABELS } from './adminAccessMatrix';

function StatCard({ icon: Icon, label, value, hint, to, testId }) {
  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-amber-400" />
        </div>
        {to && (
          <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-amber-300 transition-colors" aria-hidden="true" />
        )}
      </div>
      <div className="mt-4">
        <div className="text-3xl font-bold text-white tabular-nums">{value ?? '—'}</div>
        <div className="text-xs text-slate-400 uppercase tracking-wider mt-0.5">{label}</div>
        {hint && <div className="text-[11px] text-slate-500 mt-1.5">{hint}</div>}
      </div>
    </>
  );
  // Click target → deep-links to the relevant management module so the
  // metric is genuinely actionable (click "Licenses" → jump to logs).
  const className = `group bg-slate-900/70 border border-slate-800 rounded-xl p-5 transition-colors ${
    to ? 'hover:border-amber-500/40 hover:bg-slate-900 cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400' : ''
  } block`;
  if (to) {
    return (
      <Link to={to} className={className} data-testid={testId} aria-label={`${label} — open module`}>
        {body}
      </Link>
    );
  }
  return (
    <div className={className} data-testid={testId}>
      {body}
    </div>
  );
}

function SystemStatusCard({ status, detail }) {
  // Map raw status string into UI tokens. "perfect" = green, "errors" = red,
  // "unknown" = amber (e.g. fresh deploy, no diagnostic run yet).
  const map = {
    perfect: {
      label: 'Perfect',
      hint: 'All tests cleared with 0 errors on the last diagnostic.',
      Icon: CheckCircle2,
      iconCls: 'text-emerald-400',
      bg: 'bg-emerald-500/10 border-emerald-500/30',
      valueCls: 'text-emerald-300',
    },
    errors_detected: {
      label: 'Errors Detected',
      hint: 'The last diagnostic surfaced failures — review immediately.',
      Icon: AlertTriangle,
      iconCls: 'text-rose-400',
      bg: 'bg-rose-500/10 border-rose-500/30',
      valueCls: 'text-rose-300',
    },
    unknown: {
      label: 'Not Run Yet',
      hint: 'No diagnostic has been executed on this deployment yet.',
      Icon: AlertTriangle,
      iconCls: 'text-amber-400',
      bg: 'bg-amber-500/10 border-amber-500/30',
      valueCls: 'text-amber-300',
    },
  };
  const m = map[status] || map.unknown;
  const subtitle = detail?.total
    ? `${detail.passed}/${detail.total} passed${(detail.failed + detail.errors) > 0 ? ` • ${detail.failed + detail.errors} failed` : ''}`
    : m.hint;

  return (
    <Link
      to="/admin/diagnostics"
      data-testid="admin-stat-system"
      aria-label={`System status: ${m.label} — open diagnostics`}
      className="group bg-slate-900/70 border border-slate-800 rounded-xl p-5 transition-colors hover:border-amber-500/40 hover:bg-slate-900 cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400 block"
    >
      <div className="flex items-start justify-between gap-3">
        <div className={`w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 ${m.bg}`}>
          <m.Icon className={`w-5 h-5 ${m.iconCls}`} />
        </div>
        <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-amber-300 transition-colors" aria-hidden="true" />
      </div>
      <div className="mt-4">
        <div className={`text-2xl font-bold tabular-nums ${m.valueCls}`} data-testid="admin-stat-system-value">
          {m.label}
        </div>
        <div className="text-xs text-slate-400 uppercase tracking-wider mt-0.5">System</div>
        <div className="text-[11px] text-slate-500 mt-1.5">{subtitle}</div>
      </div>
    </Link>
  );
}

function SectionCard({ icon: Icon, title, description, testId, to, ready }) {
  const content = (
    <>
      <div className="w-11 h-11 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center">
        <Icon className="w-5 h-5 text-amber-400" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className="text-white font-semibold">{title}</h3>
          <span
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
              ready
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                : 'bg-amber-500/10 text-amber-400 border-amber-500/30'
            }`}
          >
            {ready ? 'Open' : 'Coming soon'}
          </span>
        </div>
        <p className="text-sm text-slate-400 mt-1">{description}</p>
      </div>
    </>
  );
  const base =
    'bg-slate-900/70 border border-slate-800 rounded-xl p-6 flex items-start gap-4 transition';
  if (to) {
    return (
      <Link to={to} data-testid={testId} className={`${base} hover:border-amber-500/40 hover:bg-slate-900`}>
        {content}
      </Link>
    );
  }
  return (
    <div data-testid={testId} className={base}>
      {content}
    </div>
  );
}

const MODULES = [
  { key: 'licenses', testId: 'admin-module-licensing', icon: Key, title: 'Licensing', description: 'Issue, revoke, and monitor application license keys and subscription plans.', to: '/admin/licenses' },
  { key: 'license_logs', testId: 'admin-module-license-logs', icon: ScrollText, title: 'Licensing Logs', description: 'Per-event audit trail for every license mutation, redemption, and upgrade.', to: '/admin/license-logs' },
  { key: 'branding', testId: 'admin-module-branding', icon: Palette, title: 'Site Branding', description: 'Customize the product logo, colors, and tagline shown to all tenants.', to: '/admin/branding' },
  { key: 'notifications', testId: 'admin-module-notifications', icon: Bell, title: 'Notifications', description: 'Broadcast announcements and banners to end-users across the platform.', to: '/admin/notifications' },
  { key: 'payments', testId: 'admin-module-payments', icon: CreditCard, title: 'Payment Integrations', description: 'Manage Razorpay (and future gateway) credentials used for in-app upgrades.', to: '/admin/payments' },
  { key: 'billing_profile', testId: 'admin-module-billing-profile', icon: Building2, title: 'Rupiwise Company Profile', description: 'Seller identity used on every licence-purchase invoice — GSTIN, address, HSN/SAC, invoice-number prefix.', to: '/admin/billing-profile' },
  { key: 'invoicing', testId: 'admin-module-invoicing', icon: FileCheck2, title: 'Invoicing & GST Reports', description: 'Search licence invoices, issue credit notes, correct-and-re-issue for buyer GSTIN edits, export monthly GSTR-1 / GSTR-3B.', to: '/admin/invoicing' },
  { key: 'wallets', testId: 'admin-module-wallets', icon: Wallet, title: 'Credit Wallets', description: 'View & manage tenant credit wallets — live balance, transaction ledger, manual credit/debit with mandatory reason, freeze/unfreeze in disputes.', to: '/admin/wallets' },
  { key: 'license_pricing', testId: 'admin-module-license-pricing', icon: CreditCard, title: 'License Pricing', description: 'Edit the one-time Pro upgrade price (₹). Requires email OTP — protects against accidental price changes.', to: '/admin/license-pricing' },
  { key: 'email_integrations', testId: 'admin-module-email', icon: Mail, title: 'Email Integrations', description: 'Configure Custom SMTP or AWS SES for system (OTPs) and customer (license) emails.', to: '/admin/email-integrations' },
  { key: 'whatsapp_integrations', testId: 'admin-module-whatsapp', icon: MessageSquare, title: 'WhatsApp Integrations', description: 'Configure MSG91 to send recovery OTPs over WhatsApp instead of the dev mock.', to: '/admin/whatsapp-integrations' },
  { key: 'email_templates', testId: 'admin-module-email-templates', icon: FileText, title: 'Email Templates', description: 'Edit subject and HTML/plaintext body of license, team-invite and other transactional emails.', to: '/admin/email-templates' },
  { key: 'email_logs', testId: 'admin-module-email-logs', icon: Mail, title: 'Email Delivery Logs', description: 'Every outbound email attempt — system, customer, and tests with retry tooling.', to: '/admin/email-logs' },
  { key: 'webhooks', testId: 'admin-module-webhooks', icon: Webhook, title: 'Website Webhook', description: 'Accept license purchases from your own website. HMAC-signed, idempotent, auto email delivery.', to: '/admin/webhooks' },
  { key: 'einvoice_gsp', testId: 'admin-module-einvoice-gsp', icon: FileCheck2, title: 'E-Invoicing GSP', description: 'Configure the global GST Suvidha Provider (Masters India / ClearTax) for IRN/QR generation.', to: '/admin/einvoice-gsp' },
  { key: 'gstin_providers', testId: 'admin-module-gstin-providers', icon: Search, title: 'GSTIN Fetch Providers', description: 'Primary + fallback provider stack for the "Fetch details" button on Customer/Vendor forms. Supports Cashfree, Sandbox.co.in, gstinapi.in with per-provider budgets and one-click test lookups.', to: '/admin/gstin-providers' },
  { key: 'diagnostics', testId: 'admin-module-diagnostics', icon: Activity, title: 'Diagnostics', description: 'One-click runner for the platform regression test suites — verify system integrity in seconds.', to: '/admin/diagnostics' },
  { key: 'central_logs', testId: 'admin-module-central-logs', icon: ScrollText, title: 'Central Logs', description: 'Cross-module audit timeline — auth, licensing, payments, email, and webhook events in one place.', to: '/admin/central-logs' },
  { key: 'user_console', testId: 'admin-module-user-console', icon: LifeBuoy, title: 'User Troubleshooting Console', description: 'Securely impersonate any end-user for support — every session is logged to an immutable audit table.', to: '/admin/user-console' },
  { key: 'user_audit_logs', testId: 'admin-module-user-audit-logs', icon: ScrollText, title: 'User Impersonation Audit', description: 'Immutable, read-only ledger of every impersonation session — admin, target, purpose, IP, and timestamp.', to: '/admin/user-audit-logs' },
  { key: 'demo_leads', testId: 'admin-module-demo-leads', icon: Rocket, title: 'Demo & Leads', description: 'Toggle the "Explore Live Demo" button on the Login page and export captured visitor leads.', to: '/admin/demo-leads' },
  // Knowledge Base Editor — surfaced inside the new Help Center page (Tab A).
  // We keep the MODULES entry so deep-links to /admin/kb-editor and access-matrix
  // checks (`canAccessTab(role, 'kb_editor')`) keep working; the card is no
  // longer rendered under any TAB_GROUP.
  { key: 'kb_editor', testId: 'admin-module-kb-editor', icon: BookOpen, title: 'Knowledge Base Editor', description: 'Author and edit the SupportArticles that power the AI Omnibar documentation drawer.', to: '/admin/kb-editor' },
  // Help Center — direct-navigate row in the sidebar. Has no detail
  // cards on the dashboard; clicking the tab opens the dedicated page.
  { key: 'help_center', testId: 'admin-module-help-center', icon: LifeBuoy, title: 'Help Center', description: 'Knowledge base editor + cross-tenant ticket triage with 6-hour SLA.', to: '/admin/help-center' },
];

// Vertical tab groups for the management modules — collapses 12 cards into
// 6 themed sections so the dashboard scrolls less. Tab visibility is
// derived from the access matrix: a tab shows iff at least one of its
// modules is accessible to the current role.
const TAB_GROUPS = [
  { key: 'licensing',      title: 'Licensing',      icon: Key,           moduleKeys: ['license_pricing', 'licenses', 'license_logs'] },
  { key: 'communications', title: 'Communications', icon: Mail,          moduleKeys: ['email_integrations', 'whatsapp_integrations', 'email_templates', 'email_logs'] },
  { key: 'branding',       title: 'Branding',       icon: Palette,       moduleKeys: ['branding'] },
  { key: 'integrations',   title: 'Integrations',   icon: Plug,          moduleKeys: ['payments', 'webhooks', 'einvoice_gsp', 'gstin_providers'] },
  // Invoicing — Phase 4 lights up: seller identity, seller-side
  // licence invoices, credit-notes, and monthly GSTR-1/GSTR-3B exports
  // (JSON + Excel) all live under this tab.
  { key: 'invoicing',      title: 'Invoicing',      icon: FileCheck2,    moduleKeys: ['billing_profile', 'invoicing'] },
  { key: 'wallets',        title: 'Credit Wallets', icon: Wallet,        moduleKeys: ['wallets'] },
  { key: 'notifications',  title: 'Notifications',  icon: Megaphone,     moduleKeys: ['notifications'] },
  { key: 'monitoring',     title: 'Monitoring',     icon: MonitorCheck,  moduleKeys: ['diagnostics', 'central_logs'] },
  { key: 'support',        title: 'Support',        icon: LifeBuoy,      moduleKeys: ['user_console', 'user_audit_logs'] },
  { key: 'growth',         title: 'Growth',         icon: Rocket,        moduleKeys: ['demo_leads'] },
  // Help Center — `directNavigate` tells the sidebar to route straight
  // to /admin/help-center instead of toggling an in-page card view.
  // `moduleKeys` references the help_center pseudo-module purely so the
  // tab passes the `modules.length > 0` visibility filter; no card is
  // rendered because we short-circuit on directNavigate.
  { key: 'help_center',    title: 'Help Center',    icon: LifeBuoy,      moduleKeys: ['help_center'], directNavigate: '/admin/help-center' },
];

export function AdminDashboardPage() {
  const { admin, loading, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!admin) return;
    adminApi
      .get('/admin/stats')
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, [admin]);

  // All hooks must be declared BEFORE any conditional return so React's
  // hook-call order stays stable across renders (otherwise: "Rendered
  // more hooks than during the previous render").
  const role = admin?.role || 'superadmin';
  const moduleByKey = useMemo(() => Object.fromEntries(MODULES.map((m) => [m.key, m])), []);
  const visibleTabs = useMemo(() =>
    TAB_GROUPS.map((g) => ({
      ...g,
      modules: g.moduleKeys
        .map((k) => moduleByKey[k])
        .filter((m) => m && canAccessTab(role, m.key)),
    })).filter((g) => g.modules.length > 0),
  [role, moduleByKey]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isSuperAdmin = role === 'superadmin';
  const canSeeProfile = canAccessTab(role, 'profile');
  const canSeeTeam = canAccessTab(role, 'team');
  const canSeeHelp = canAccessTab(role, 'help_center');
  const canSeeStats = isSuperAdmin; // role-restricted staff don't see system-wide stats

  // Active tab is reflected in the URL (`?tab=licensing`) so the SuperAdmin
  // can deep-link / refresh and stay in context. Falls back to the first
  // accessible tab.
  const requestedTab = searchParams.get('tab');
  // Direct-navigate tabs (Help Center) never become the in-page active
  // tab — they route away. Pick the first content-bearing tab instead
  // so the dashboard always has a sensible default detail pane.
  const activeTab = visibleTabs.find((t) => !t.directNavigate && t.key === requestedTab)
    || visibleTabs.find((t) => !t.directNavigate);
  const setActiveTab = (key) => setSearchParams({ tab: key }, { replace: true });

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen"
      style={{
        background:
          'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)',
      }}
    >
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <Shield className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <div className="text-white font-semibold">SuperAdmin Panel</div>
              <div className="text-xs text-slate-400">
                Signed in as <span data-testid="admin-email" className="text-amber-400">{admin.email}</span>
                <span className="mx-1.5 text-slate-600">·</span>
                <span data-testid="admin-role-label" className="text-slate-300">{ROLE_LABELS[role] || role}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="admin-settings-trigger"
                  className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white"
                >
                  <Settings className="w-4 h-4 mr-2" /> Settings <ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800 text-slate-100 min-w-[200px]" data-testid="admin-settings-menu">
                <DropdownMenuLabel className="text-slate-400 text-[10px] uppercase tracking-wider">Account</DropdownMenuLabel>
                {canSeeProfile && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin/profile" data-testid="admin-settings-profile" className="cursor-pointer focus:bg-slate-800 focus:text-white">
                      <UserCircle className="w-4 h-4 mr-2 text-amber-400" /> My Profile
                    </Link>
                  </DropdownMenuItem>
                )}
                {canSeeTeam && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin/team" data-testid="admin-settings-team" className="cursor-pointer focus:bg-slate-800 focus:text-white">
                      <Users className="w-4 h-4 mr-2 text-amber-400" /> Admin Team
                    </Link>
                  </DropdownMenuItem>
                )}
                {(canSeeProfile || canSeeTeam) && (
                  <>
                    <DropdownMenuSeparator className="bg-slate-800" />
                    <DropdownMenuLabel className="text-slate-400 text-[10px] uppercase tracking-wider italic">
                      Help Center moved to sidebar →
                    </DropdownMenuLabel>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white tracking-tight">
            Welcome back, {admin.name || 'Administrator'}
          </h1>
          <p className="text-slate-400 mt-1">
            {isSuperAdmin
              ? 'High-level system oversight and configuration.'
              : `You're signed in as ${ROLE_LABELS[role] || role}. Only the modules relevant to your role are shown below.`}
          </p>
        </div>

        {/* Stats — SuperAdmin only */}
        {canSeeStats && (
          <section className="mb-10" data-testid="admin-stats-section">
            <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">
              System overview
            </h2>
            {statsLoading ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading stats…
              </div>
            ) : stats ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                <StatCard
                  testId="admin-stat-organizations"
                  icon={Users}
                  label="Users / Organizations"
                  value={stats.organizations}
                  hint="Total organizations on the platform"
                  to="/admin?tab=licensing"
                />
                <StatCard
                  testId="admin-stat-licenses"
                  icon={Key}
                  label="Licenses"
                  value={stats.licenses}
                  hint="Total licenses ever issued by the system"
                  to="/admin/license-logs"
                />
                <SystemStatusCard
                  status={stats.system_status}
                  detail={stats.system_detail}
                />
                <StatCard
                  testId="admin-stat-team"
                  icon={Shield}
                  label="Team"
                  value={stats.team}
                  hint="Admin panel members (incl. SuperAdmin)"
                  to="/admin/team"
                />
                <StatCard
                  testId="admin-stat-leads-24h"
                  icon={Rocket}
                  label="Captured Leads (24h)"
                  value={stats.leads_last_24h}
                  hint="Demo leads captured in the last 24 hours"
                  to="/admin/demo-leads"
                />
                <StatCard
                  testId="admin-stat-open-tickets"
                  icon={Inbox}
                  label="Open Support Tickets"
                  value={stats.open_tickets}
                  hint="Tickets awaiting triage or in progress"
                  to="/admin/help-center"
                />
              </div>
            ) : (
              <div className="text-slate-500 text-sm">Unable to load stats.</div>
            )}
          </section>
        )}

        {/* Modules — vertical tab layout */}
        <section data-testid="admin-modules-section">
          <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">
            Management modules
          </h2>
          {visibleTabs.length === 0 ? (
            <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-8 text-center">
              <LifeBuoy className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              <h3 className="text-white font-semibold">No modules assigned yet</h3>
              <p className="text-sm text-slate-400 mt-1">
                Your role only grants access to the Help Center for now. Use the Settings menu to open it.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-4 lg:gap-6">
              {/* Vertical tab bar (browser-style) */}
              <nav
                className="col-span-12 md:col-span-3 lg:col-span-3"
                aria-label="Module categories"
                data-testid="admin-modules-tabs"
              >
                <ul className="bg-slate-900/70 border border-slate-800 rounded-xl p-1.5 flex md:flex-col md:overflow-visible overflow-x-auto gap-1">
                  {visibleTabs.map((t) => {
                    const Icon = t.icon;
                    const isActive = activeTab?.key === t.key;
                    const handleClick = () => {
                      // Direct-navigate tabs (e.g. Help Center) route straight
                      // to a dedicated page instead of toggling the in-page
                      // card view. Keeps the sidebar usable as a primary nav.
                      if (t.directNavigate) {
                        navigate(t.directNavigate);
                        return;
                      }
                      setActiveTab(t.key);
                    };
                    return (
                      <li key={t.key} className="md:w-full">
                        <button
                          type="button"
                          onClick={handleClick}
                          data-testid={`admin-tab-${t.key}`}
                          aria-current={isActive ? 'page' : undefined}
                          className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left whitespace-nowrap ${
                            isActive
                              ? 'bg-amber-500/10 border border-amber-500/30 text-amber-300'
                              : 'border border-transparent text-slate-300 hover:bg-slate-800/60 hover:text-white'
                          }`}
                        >
                          <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-amber-400' : 'text-slate-400 group-hover:text-amber-300'}`} />
                          <span className="flex-1 font-medium">{t.title}</span>
                          {!t.directNavigate ? (
                            <>
                              <span className={`hidden md:inline text-[10px] font-mono px-1.5 py-0.5 rounded border ${isActive ? 'border-amber-500/30 text-amber-300' : 'border-slate-700 text-slate-500'}`}>
                                {t.modules.length}
                              </span>
                              <ChevronRight className={`hidden md:block w-3.5 h-3.5 shrink-0 ${isActive ? 'text-amber-400' : 'text-slate-600 group-hover:text-amber-300'}`} />
                            </>
                          ) : (
                            <ChevronRight className="hidden md:block w-3.5 h-3.5 shrink-0 text-slate-500 group-hover:text-amber-300" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              {/* Active-tab content pane */}
              <div
                className="col-span-12 md:col-span-9 lg:col-span-9 space-y-3"
                role="tabpanel"
                aria-labelledby={activeTab ? `admin-tab-${activeTab.key}` : undefined}
                data-testid={`admin-tabpanel-${activeTab?.key || 'empty'}`}
              >
                {activeTab && (
                  <>
                    <div className="flex items-baseline justify-between gap-3 px-1">
                      <h3 className="text-white font-semibold text-base flex items-center gap-2">
                        <span className="w-1.5 h-5 rounded bg-amber-400" />
                        {activeTab.title}
                      </h3>
                      <p className="text-xs text-slate-500">
                        {activeTab.modules.length} module{activeTab.modules.length === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="grid gap-3">
                      {activeTab.modules.map((m) => (
                        <SectionCard
                          key={m.key}
                          testId={m.testId}
                          icon={m.icon}
                          title={m.title}
                          description={m.description}
                          to={m.to}
                          ready
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
